import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { cn } from '@/lib/utils'
import {
    getPresignedUrl,
    UploadedFile,
    uploadFileToPresignedUrl,
} from '@/modules/Asset/services'
import { sanitizeFileName } from '@/utils/function'
import {
    AlertCircle,
    CheckCircle,
    FileText,
    Loader2,
    Upload,
    X,
} from 'lucide-react'
import React, { useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { AssetFormData } from '.'

const MAX_FILE_SIZE = 50 * 1024 * 1024 // 50MB

interface FileUploadItem {
    file: File
    status: 'pending' | 'uploading' | 'success' | 'error'
    progress: number
    uploadedFile?: UploadedFile
    error?: string
}

interface Step2UploadFilesProps {
    formData: AssetFormData
    setFormData: React.Dispatch<React.SetStateAction<AssetFormData>>
}

const Step2UploadFiles: React.FC<Step2UploadFilesProps> = ({
    formData,
    setFormData,
}) => {
    const { t } = useTranslation('asset')
    const [fileUploads, setFileUploads] = useState<FileUploadItem[]>([])
    const [isUploading, setIsUploading] = useState(false)
    const [dragActive, setDragActive] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)
    const isInitialized = useRef(false)

    // Initialize file uploads from existing formData (runs once when data arrives)
    React.useEffect(() => {
        if (isInitialized.current) return
        if (formData.uploadedFiles && formData.uploadedFiles.length > 0) {
            isInitialized.current = true
            setFileUploads(
                formData.uploadedFiles.map((file) => ({
                    file: new File([], file.file_name, {
                        type: file.file_type,
                    }),
                    status: 'success' as const,
                    progress: 100,
                    uploadedFile: file,
                }))
            )
        }
    }, [formData.uploadedFiles])

    // Mutation for getting presigned URL and uploading file
    const uploadFileMutation = useMutationWithHandler({
        mutationFn: async (file: File) => {
            // Get presigned URL
            const presignedResponse = await getPresignedUrl({
                file_name: sanitizeFileName(file.name),
                file_type: file.type,
                is_temporary: true,
            })

            // Upload file to presigned URL
            await uploadFileToPresignedUrl(
                presignedResponse.result.presigned_url,
                file
            )

            // Return uploaded file info
            const uploadedFile: UploadedFile = {
                file_name: file.name,
                file_path: presignedResponse.result.temp_path, // Store temp_path as file_path
                file_size: file.size,
                file_type: file.type,
                uploaded_at: new Date().toISOString(),
            }

            return uploadedFile
        },
    })

    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true)
        } else if (e.type === 'dragleave') {
            setDragActive(false)
        }
    }

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        setDragActive(false)

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const files = Array.from(e.dataTransfer.files)
            handleFiles(files)
        }
    }

    const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const files = Array.from(e.target.files)
            handleFiles(files)
        }
    }

    const handleFiles = async (files: File[]) => {
        const isRegulation = formData.documentType === 'REGULATION'

        // Filter out folders and validate files
        const validFiles = files.filter((file) => {
            if (!file.type && file.size === 0) {
                toast.error(`"${file.name}" ${t('form.folderUploadError')}`)
                return false
            }
            if (file.size > MAX_FILE_SIZE) {
                toast.error(
                    t('form.fileSizeExceeded', {
                        name: file.name,
                        limit: '50MB',
                    })
                )
                return false
            }

            // PDF restriction for Regulation
            if (isRegulation && file.type !== 'application/pdf') {
                toast.error(
                    t('form.pdfOnlyError', {
                        defaultValue:
                            'Only PDF files are allowed for Regulation documents',
                    })
                )
                return false
            }

            return true
        })

        if (validFiles.length === 0) return

        // Dedup by file_name: uploading a file with the same name as an existing
        // entry replaces the old one (avoids UI duplicates + server storing both).
        const newNames = new Set(validFiles.map((f) => f.name))

        const newFileUploads: FileUploadItem[] = validFiles.map((file) => ({
            file,
            status: 'pending' as const,
            progress: 0,
        }))

        setFileUploads((prev) => [
            ...prev.filter((item) => !newNames.has(item.file.name)),
            ...newFileUploads,
        ])
        setFormData((prev) => ({
            ...prev,
            files: [
                ...prev.files.filter((f) => !newNames.has(f.name)),
                ...validFiles,
            ],
            uploadedFiles: (prev.uploadedFiles || []).filter(
                (u) => !newNames.has(u.file_name)
            ),
        }))

        // Start uploading files
        setIsUploading(true)
        const uploadedFiles: UploadedFile[] = []

        for (let i = 0; i < validFiles.length; i++) {
            const file = validFiles[i]
            const uploadIndex = fileUploads.length + i

            try {
                // Update status to uploading
                setFileUploads((prev) =>
                    prev.map((item, idx) =>
                        idx === uploadIndex
                            ? { ...item, status: 'uploading', progress: 50 }
                            : item
                    )
                )

                // Upload file
                const uploadedFile = await uploadFileMutation.mutateAsync(file)

                if (uploadedFile) {
                    uploadedFiles.push(uploadedFile)

                    // Update status to success
                    setFileUploads((prev) =>
                        prev.map((item, idx) =>
                            idx === uploadIndex
                                ? {
                                      ...item,
                                      status: 'success',
                                      progress: 100,
                                      uploadedFile,
                                  }
                                : item
                        )
                    )
                } else {
                    // mutateAsync swallows errors and returns undefined on failure
                    // (e.g. S3 CORS/network reject). Reflect the real failure in the
                    // UI so the "at least one file" gate isn't silently blocked by a
                    // file that looks "Uploaded" but was never stored server-side.
                    setFileUploads((prev) =>
                        prev.map((item, idx) =>
                            idx === uploadIndex
                                ? {
                                      ...item,
                                      status: 'error',
                                      progress: 0,
                                      error: t('form.uploadFailed'),
                                  }
                                : item
                        )
                    )
                }
            } catch (error) {
                const errorMessage =
                    error instanceof Error
                        ? error.message
                        : t('form.uploadFailed')

                setFileUploads((prev) =>
                    prev.map((item, idx) =>
                        idx === uploadIndex
                            ? {
                                  ...item,
                                  status: 'error',
                                  error: errorMessage,
                              }
                            : item
                    )
                )

                console.error('[AssetUpload]', file.name, error)
            }
        }

        // Update form data with uploaded files
        setFormData((prev) => ({
            ...prev,
            uploadedFiles: [...(prev.uploadedFiles || []), ...uploadedFiles],
        }))

        setIsUploading(false)
    }

    const removeFile = (index: number) => {
        const removedUpload = fileUploads[index]

        setFileUploads((prev) => prev.filter((_, i) => i !== index))
        setFormData((prev) => ({
            ...prev,
            files: prev.files.filter((_, i) => i !== index),
            uploadedFiles: prev.uploadedFiles?.filter(
                (file) => file.file_name !== removedUpload.file.name
            ),
        }))
    }

    return (
        <Card>
            <CardHeader>
                <CardTitle>{t('form.uploadDocuments')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                <div
                    className={cn(
                        'border-2 border-dashed rounded-lg p-4 sm:p-6 md:p-8 text-center transition-colors',
                        dragActive
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-300 hover:border-gray-400'
                    )}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    data-tour="upload-area"
                >
                    <Upload className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-gray-400" />
                    <p className="mt-2 text-sm text-gray-600">
                        {t('form.clickToUpload')}
                    </p>
                    <p className="text-xs text-gray-500">
                        {formData.documentType === 'REGULATION'
                            ? t('form.fileRequirementsPDF')
                            : t('form.fileRequirements')}
                    </p>
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        onChange={handleFileInput}
                        className="hidden"
                        accept={
                            formData.documentType === 'REGULATION'
                                ? '.pdf'
                                : '.pdf,.png,.jpg,.jpeg,.doc,.docx'
                        }
                    />
                    <Button
                        variant="outline"
                        className="mt-4"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isUploading}
                    >
                        {isUploading ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                {t('form.uploading')}
                            </>
                        ) : (
                            t('form.selectFiles')
                        )}
                    </Button>
                </div>

                {fileUploads.length > 0 && (
                    <div className="space-y-2">
                        <h4 className="text-sm font-medium">
                            {t('form.files')} ({fileUploads.length}):
                        </h4>
                        {fileUploads.map((upload, index) => (
                            <div
                                key={index}
                                className={cn(
                                    'flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 rounded gap-2',
                                    upload.status === 'success' &&
                                        'bg-green-50 border border-green-200',
                                    upload.status === 'error' &&
                                        'bg-red-50 border border-red-200',
                                    upload.status === 'uploading' &&
                                        'bg-blue-50 border border-blue-200',
                                    upload.status === 'pending' &&
                                        'bg-gray-50 border border-gray-200'
                                )}
                            >
                                <div className="flex items-center space-x-2 min-w-0 flex-1">
                                    {upload.status === 'success' && (
                                        <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                                    )}
                                    {upload.status === 'error' && (
                                        <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0" />
                                    )}
                                    {upload.status === 'uploading' && (
                                        <Loader2 className="h-4 w-4 text-blue-600 flex-shrink-0 animate-spin" />
                                    )}
                                    {upload.status === 'pending' && (
                                        <FileText className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm truncate">
                                            {upload.file.name}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                            {(
                                                (upload.uploadedFile
                                                    ?.file_size ||
                                                    upload.file.size) / 1024
                                            ).toFixed(2)}{' '}
                                            KB
                                            {upload.status === 'uploading' &&
                                                ` - Uploading...`}
                                            {upload.status === 'success' &&
                                                ` - Uploaded`}
                                            {upload.status === 'error' &&
                                                ` - ${upload.error}`}
                                        </p>
                                    </div>
                                </div>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => removeFile(index)}
                                    disabled={upload.status === 'uploading'}
                                    className="flex-shrink-0"
                                >
                                    <X className="h-4 w-4" />
                                </Button>
                            </div>
                        ))}
                    </div>
                )}
            </CardContent>
        </Card>
    )
}

export default Step2UploadFiles
